import { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams, useParams } from 'react-router-dom'
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material'
import {
  RoomType,
  BusinessProfile,
  DatingProfile,
  JobProfile,
} from '@/types/profile'
import { useMyProfile } from '@/contexts/ProfileContext'
import { ProfileTabForm, ProfileTabFormHandle } from '@/components/ProfileTabForm'
import { getProfile } from '@/services/profileService'
import { toRoomType } from '@/utils/roomType'

interface ProfileFormPageProps {
  mode: 'register' | 'edit'
}

export const ProfileFormPage = ({ mode }: ProfileFormPageProps) => {
  const isEditMode = mode === 'edit'
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profileId } = useParams<{ profileId: string }>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { myProfiles } = useMyProfile()

  const initialType = toRoomType(searchParams.get('type'))
  const [currentRoomType, setCurrentRoomType] = useState<RoomType>(initialType)
  const [isLoading, setIsLoading] = useState(false)
  const [initialProfile, setInitialProfile] = useState<BusinessProfile | DatingProfile | JobProfile | null>(null)

  const formRef = useRef<ProfileTabFormHandle>(null)

  const isRegistered = (type: RoomType) => myProfiles[type] !== null

  // editモードの場合、既存プロフィールを読み込む
  useEffect(() => {
    const fetchProfile = async () => {
      if (!isEditMode || !profileId) return
      
      try {
        setIsLoading(true)
        const profileData = await getProfile(profileId)
        
        // APIレスポンスをProfile型にマッピング
        const profileType = toRoomType(profileData.eventType)
        const baseProfile = {
          id: profileData.userId,
          type: profileType,
          pr: profileData.prText,
          photoUrl:
            profileType === 'business'
              ? profileData.bizPhotoUrl
              : profileType === 'dating'
                ? profileData.lovePhotoUrl
                : profileData.careerPhotoUrl,
          createdAt: new Date(),
        }

        let mappedProfile: BusinessProfile | DatingProfile | JobProfile

        if (profileType === 'business') {
          mappedProfile = {
            ...baseProfile,
            type: 'business' as const,
            name: profileData.bizName || '',
            companyName: profileData.bizCompanyName || '',
            position: profileData.bizPosition || '',
            businessContent: profileData.bizContent || '',
            comment: profileData.bizComment || '',
            wantToMatch: profileData.bizTargetIndustry || '',
            notWantToMatch: profileData.bizExcludeIndustry || '',
            contactEmail: profileData.isEmailPublic ? profileData.bizEmail || profileData.mailAddress || '' : '',
            contactPhone: profileData.isTelPublic ? profileData.bizTel || '' : '',
          }
        } else if (profileType === 'dating') {
          mappedProfile = {
            ...baseProfile,
            type: 'dating' as const,
            name: profileData.loveNickname || '',
            age: profileData.loveAge || 0,
            occupation: profileData.loveJob || '',
            gender: profileData.loveGender as any,
            annualIncome: profileData.loveAnnualIncome || '',
            wantChildren: profileData.loveWantsKids || '',
            wantToMatch: profileData.loveTargetPerson || '',
            notWantToMatch: profileData.loveExcludePerson || '',
            contactEmail: profileData.isEmailPublic ? profileData.loveContactInfo || profileData.mailAddress || '' : '',
            contactPhone: profileData.isTelPublic ? profileData.loveTel || '' : '',
            userNumber: profileData.userId,
          }
        } else {
          mappedProfile = {
            ...baseProfile,
            type: 'job' as const,
            name: profileData.careerName || '',
            companyName: profileData.careerPrevCompany || '',
            lastPosition: profileData.careerLastPosition || '',
            education: profileData.careerEducationLevel || '',
            schoolName: profileData.careerSchoolName || '',
            age: profileData.careerAge || 0,
            gender: profileData.careerGender as any,
            comment: profileData.careerComment || '',
            wantToMatch: profileData.careerTargetCompany || '',
            notWantToMatch: profileData.careerExcludeConditions || '',
            contactEmail: profileData.isEmailPublic ? profileData.careerEmail || profileData.mailAddress || '' : '',
            contactPhone: profileData.isTelPublic ? profileData.careerTel || '' : '',
          }
        }

        setInitialProfile(mappedProfile)
        setCurrentRoomType(mappedProfile.type)
      } catch (err) {
        console.error('Failed to fetch profile:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchProfile()
  }, [isEditMode, profileId])

  const submitLabel = isEditMode
    ? (isRegistered(currentRoomType) ? '確認画面へ（更新）' : '確認画面へ（新規登録）')
    : '確認画面へ進む'

  const handleValidSubmit = (profile: BusinessProfile | DatingProfile | JobProfile, pendingPhoto?: { fileBase64: string; fileName: string } | null) => {
    navigate('/profile/confirm', { state: { profile, mode, pendingPhoto: pendingPhoto ?? null } })
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    const ok = await (formRef.current?.submit() ?? Promise.resolve(false))
    if (!ok) setIsLoading(false)
  }

  const renderHeader = () => (
    <AppBar position="sticky" sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Toolbar sx={{ px: { xs: 1.5, sm: 2 } }}>
        {isEditMode ? (
          <>
            <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              マイプロフィール編集
            </Typography>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BoltIcon sx={{ color: 'white', fontSize: { xs: 18, sm: 20 } }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Comyuless
            </Typography>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  )

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {renderHeader()}

      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Card>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {!isEditMode && (
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                component="h1"
                gutterBottom
                fontWeight={700}
                textAlign="center"
              >
                プロフィール登録
              </Typography>
            )}

            <ProfileTabForm
              ref={formRef}
              mode={mode}
              initialRoomType={initialType}
              initialProfile={initialProfile}
              onValidSubmit={handleValidSubmit}
              onRoomTypeChange={setCurrentRoomType}
            />

            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={isLoading}
              onClick={handleSubmit}
              sx={{ mt: 2 }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : submitLabel}
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}
